import { doc, getDoc } from "firebase/firestore";
import { firestoreDb } from "../App";

/**
 * Public asset base URL lives in the shared Firestore doc `app_config/assets`
 * (also read by the TV app). Stored objects reference assets by object key;
 * the full URL is `assetBaseUrl + "/" + key`.
 */
const APP_CONFIG_COLLECTION = "app_config";
const ASSETS_CONFIG_DOC = "assets";

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

export const isAbsoluteUrl = (value: string): boolean =>
  ABSOLUTE_URL_PATTERN.test(value);

const trimTrailingSlashes = (value: string): string =>
  value.replace(/\/+$/, "");

let assetBaseUrlPromise: Promise<string> | null = null;

const loadAssetBaseUrl = async (): Promise<string> => {
  const snap = await getDoc(
    doc(firestoreDb, APP_CONFIG_COLLECTION, ASSETS_CONFIG_DOC)
  );
  const value = snap.data()?.assetBaseUrl;

  if (typeof value !== "string" || !value.trim()) {
    throw new Error(
      `Missing "assetBaseUrl" in Firestore ${APP_CONFIG_COLLECTION}/${ASSETS_CONFIG_DOC}`
    );
  }

  if (!/^https?:\/\/[^/\s]+/i.test(value.trim())) {
    throw new Error(
      `Invalid "assetBaseUrl" in Firestore ${APP_CONFIG_COLLECTION}/${ASSETS_CONFIG_DOC}: expected an absolute http(s) URL`
    );
  }

  return trimTrailingSlashes(value.trim());
};

/** Reads `app_config/assets` once per page load; a failed read is retried on the next call. */
export const getAssetBaseUrl = (): Promise<string> => {
  if (!assetBaseUrlPromise) {
    assetBaseUrlPromise = loadAssetBaseUrl().catch((e) => {
      assetBaseUrlPromise = null;
      throw e;
    });
  }

  return assetBaseUrlPromise;
};

/** Test-only: drops the cached base URL. */
export const resetAssetBaseUrlCache = (): void => {
  assetBaseUrlPromise = null;
};

/** Object key (or legacy absolute URL) -> loadable URL. */
export const toAssetUrl = (
  keyOrUrl: string | undefined | null,
  baseUrl: string
): string => {
  const value = (keyOrUrl || "").trim();
  if (!value) {
    return "";
  }

  if (isAbsoluteUrl(value)) {
    return value;
  }

  return `${trimTrailingSlashes(baseUrl)}/${value.replace(/^\/+/, "")}`;
};

export interface AssetReference {
  imageKey?: string;
}

export const hasWodImage = (wod: AssetReference | null | undefined): boolean =>
  Boolean(wod?.imageKey?.trim());

/** Loadable URL for a doc's `imageKey`; empty until the base URL is known. */
export const resolveWodImage = (
  wod: AssetReference | null | undefined,
  baseUrl?: string
): string => {
  const key = (wod?.imageKey || "").trim();

  if (!key || (!baseUrl && !isAbsoluteUrl(key))) {
    return "";
  }

  return toAssetUrl(key, baseUrl || "");
};
